import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import AOS from "aos";
import "aos/dist/aos.css";

export const TermsOfUse = () => {
  useEffect(() => {
    AOS.init({
      duration: 900,
      easing: "ease-out-cubic",
      once: false,
    });
  }, []);

  const colors = {
    background: "#ffffff",
    darkBackground: "#f1f1f1",
    textPrimary: "#1c1c1c",
    textSecondary: "#6b6b6b",
    accent: "#1c1c1c",
  };

  const containerStyle = {
    padding: "64px 16px",
    backgroundColor: colors.background,
    fontFamily: "Inter, system-ui, sans-serif",
  };

  const sectionStyle = (isDark) => ({
    backgroundColor: isDark ? colors.darkBackground : "#ffffff",
    borderRadius: "10px",
    padding: "48px 32px",
    marginBottom: "24px",
    boxShadow: "0px 2px 10px rgba(28,28,28,0.05)",
  });

  const headingStyle = {
    textAlign: "center",
    fontSize: "2.4rem",
    fontWeight: 700,
    color: colors.textPrimary,
    marginBottom: "32px",
  };

  const titleStyle = {
    fontSize: "1.3rem",
    fontWeight: 600,
    color: colors.textPrimary,
    marginBottom: "12px",
  };

  const textStyle = {
    color: colors.textSecondary,
    lineHeight: 1.7,
    fontSize: "1rem",
  };

  // const listStyle = {
  //   paddingLeft: "20px",
  //   marginBottom: "16px",
  // };

  return (
    <>
      <Helmet>
        <title>Terms of Use | Biblivium</title>
        <meta
          name="description"
          content="Read the Terms of Use for Biblivium, outlining the rules, responsibilities, and legal guidelines for using our platform."
        />
      </Helmet>

      <main style={containerStyle}>
        <div data-aos="zoom-in" data-aos-delay="100">
          <h1 style={headingStyle}>Terms of Use</h1>

          <div style={sectionStyle(false)}>
            <p style={textStyle}>
              Welcome to <strong>Biblivium</strong>. By accessing or using our website and services, you agree to be bound by these Terms of Use. These terms are intended to ensure a respectful, secure, and thoughtful environment aligned with the spirit of our platform. Please read them carefully before using Biblivium.</p>
          </div>

          <div style={sectionStyle(true)}>
            <h2 style={titleStyle}>1. Use of the Platform</h2>
            <p style={textStyle}>
              Biblivium is provided for personal, educational, and research purposes. You may access and explore our content to deepen your understanding of literature and culture. However, commercial use, reproduction, redistribution, or modification of our content without prior written permission is not permitted.</p>
            <p style={textStyle}>
              You agree not to engage in any activity that disrupts, damages, or interferes with the proper functioning of the website or the experience of other users. Any misuse of the platform, including attempts to gain unauthorized access or compromise security, is strictly prohibited.</p>
          </div>

          <div style={sectionStyle(false)}>
            <h2 style={titleStyle}>2. Intellectual Property</h2>
            <p style={textStyle}>
              All content available on Biblivium, including text, summaries, design elements, layout, branding, and original materials, is the intellectual property of Biblivium or its licensors. This content is protected by applicable copyright and intellectual property laws.</p>
            <p style={textStyle}>
              Unauthorized copying, distribution, reproduction, or adaptation of any material from the platform without explicit permission is prohibited and may result in legal action.</p>
          </div>

          <div style={sectionStyle(true)}>
            <h2 style={titleStyle}>3. User-Generated Content</h2>
            <p style={textStyle}>
              If you submit content to Biblivium, including messages, comments, or other materials, you remain responsible for that content. Submissions must not be unlawful, harmful, defamatory, infringing, or otherwise inappropriate.</p>
            <p style={textStyle}>
              We reserve the right to review, moderate, or remove any submitted content that violates these Terms or compromises the integrity of the platform. Submission of content does not grant you ownership of the platform or its materials.</p>
          </div>

          <div style={sectionStyle(false)}>
            <h2 style={titleStyle}>4. Privacy</h2>
            <p style={textStyle}>
              Your use of Biblivium is also governed by our <a href="/privacy-policy" style={{ color: colors.accent }}>Privacy Policy</a>, which explains how we collect, use, and safeguard your information. By accessing or using the platform, you acknowledge and agree to the practices described in that policy.</p>
          </div>

          <div style={sectionStyle(true)}>
            <h2 style={titleStyle}>5. Limitations of Liability</h2>
            <p style={textStyle}>
              Biblivium is provided on an “as is” and “as available” basis without warranties of any kind, whether express or implied. While we strive for accuracy and clarity, we do not guarantee that the content will be error-free, complete, or continuously available.</p>
            <p style={textStyle}>
              To the fullest extent permitted by law, Biblivium shall not be liable for any direct, indirect, incidental, or consequential damages arising from the use of, or inability to use, the platform or reliance on its content.</p>
          </div>

          <div style={sectionStyle(false)}>
            <h2 style={titleStyle}>6. Changes to Terms</h2>
            <p style={textStyle}>
              We may update these Terms of Use periodically to reflect changes in operations, legal requirements, or platform development. Updated terms will be published on this page. Continued use of the website after changes are posted constitutes acceptance of the revised Terms.</p>
          </div>

          <div style={sectionStyle(true)}>
            <h2 style={titleStyle}>7. Contact Us</h2>
            <p style={textStyle}>
              If you have questions regarding these Terms of Use, please contact us at <a href="mailto:info@lonewolfwrites.com" style={{ color: colors.accent }}>info@lonewolfwrites.com</a>. We are committed to maintaining clarity and fairness in all aspects of our platform.</p>
          </div>
        </div>
      </main>
    </>
  );
};
